import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, FileArchive, TrendingDown, Timer } from "lucide-react";
import { api } from "@/lib/api";

function fmtSeconds(s) {
  if (s == null) return "—";
  if (s >= 60) return `${(s / 60).toFixed(2)} min`;
  return `${s.toFixed(1)} s`;
}

export default function MTTRPanel({ incidentId, refreshKey, archived }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .mttrReport(incidentId)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [incidentId, refreshKey]);

  if (loading) {
    return (
      <div className="cre-surface rounded-sm p-6 text-sm text-zinc-500" data-testid="mttr-loading">
        Computing MTTR savings…
      </div>
    );
  }
  if (!report) return null;

  const baseline = report.manual_baseline_minutes;
  const cre = report.cre_total_minutes;
  const cre_pct = Math.min(100, (cre / Math.max(baseline, 1)) * 100);

  return (
    <div className="cre-surface rounded-sm p-6" data-testid="mttr-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="cre-overline text-emerald-300">Post-incident report</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-1">
            MTTR savings vs manual baseline
          </h3>
        </div>
        <a
          href={api.mttrPdfUrl(incidentId)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mttr-download-pdf"
        >
          <Button
            variant="outline"
            className="rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
          >
            <Download className="h-4 w-4 mr-2" strokeWidth={1.75} />
            Export executive PDF
          </Button>
        </a>
      </div>

      {archived ? (
        <div
          className="mt-3 flex items-center gap-2 text-xs"
          data-testid="mttr-archive-status"
        >
          <FileArchive className="h-3.5 w-3.5 text-blue-300" strokeWidth={1.5} />
          <span className="text-zinc-300">
            Archived to object storage on approval ·{" "}
            <span className="font-mono text-zinc-500">
              {Math.round((archived.size || 0) / 1024)} KB
            </span>
          </span>
          <a
            href={api.mttrArchivedUrl(incidentId)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mttr-archived-link"
            className="ml-auto font-mono text-blue-300 hover:text-blue-200 underline-offset-2 hover:underline"
          >
            View archived copy →
          </a>
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-5">
          <div className="cre-overline text-emerald-300">Time saved</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              data-testid="mttr-minutes-saved"
              className="font-heading text-5xl font-black tracking-tighter text-emerald-300"
            >
              {Math.round(report.minutes_saved)}
            </span>
            <span className="font-mono text-sm text-emerald-300/70">min</span>
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            vs the manual incident-response baseline for {report.severity}
          </div>
        </div>
        <div className="border border-white/10 rounded-sm p-5">
          <div className="cre-overline text-blue-300">MTTR reduction</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              data-testid="mttr-percent-saved"
              className="font-heading text-5xl font-black tracking-tighter text-blue-300"
            >
              {report.percent_saved}
            </span>
            <span className="font-mono text-sm text-blue-300/70">%</span>
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            faster than human-only resolution
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="cre-overline mb-2">Comparison</div>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400 flex items-center gap-1.5">
                <Timer className="h-3 w-3" /> Manual AI workspace baseline
              </span>
              <span className="font-mono text-zinc-300">{baseline} min</span>
            </div>
            <div className="mt-1 h-2 bg-white/5 rounded-sm overflow-hidden">
              <div
                className="h-full bg-rose-500/60"
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400 flex items-center gap-1.5">
                <TrendingDown className="h-3 w-3" /> CRE (current analysis)
              </span>
              <span className="font-mono text-emerald-300">
                {cre.toFixed(2)} min
              </span>
            </div>
            <div className="mt-1 h-2 bg-white/5 rounded-sm overflow-hidden">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${Math.max(2, cre_pct)}%` }}
              />
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-zinc-400">
          <div className="border border-white/10 rounded-sm p-2.5">
            <div className="cre-overline">Triage</div>
            <div className="mt-0.5 font-mono text-zinc-200">
              {report.triage_minutes.toFixed(2)} min
            </div>
          </div>
          <div className="border border-white/10 rounded-sm p-2.5">
            <div className="cre-overline">Est. controlled execution</div>
            <div className="mt-0.5 font-mono text-zinc-200">
              {report.estimated_execution_minutes.toFixed(0)} min
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="cre-overline mb-2">Phase breakdown</div>
        <div className="space-y-1.5">
          {report.phases.map((p, i) => (
            <div
              key={i}
              data-testid={`mttr-phase-${i}`}
              className="flex items-center justify-between text-xs border-b border-white/5 pb-1.5"
            >
              <div className="flex-1 min-w-0">
                <div className="text-zinc-200">{p.name}</div>
                <div className="text-zinc-500 truncate">{p.description}</div>
              </div>
              <span className="font-mono text-zinc-400 ml-3">
                {fmtSeconds(p.duration_seconds)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 text-[11px] font-mono text-slate-500 leading-relaxed">
        Estimated MTTR reduction opportunity based on simulated baseline
        comparison. Actual savings depend on approval speed, environment
        controls, and execution readiness.
      </div>
    </div>
  );
}
